import { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import type { Message } from "@ag-ui/client";
import {
  SessionContextPanelType,
  useSessionContextPanel,
} from "~/pages/manifold-nextgen/hooks/useSessionContextPanel";
import {
  AIRFLOW_RUN_TERMINAL_STATES,
  publicDatasetRunStatusPath,
} from "~/services/publicDatasetRuns";
import { apiClient } from "~/utils/apiClient";
import {
  LIST_PIPELINES_TOOL_NAME,
  parseTriggerArgs,
  resolveDagDisplayName,
  TRIGGER_RUN_TOOL_NAME,
} from "./publicDatasetTrigger";
import type { ToolCallEntry } from "./useTurns";

// TODO: third near-duplicate of this sync pattern (see useSessionIngests,
// useSessionEvalRuns) — extract a shared hook.

export interface SessionPublicDatasetRun {
  dagId: string;
  runId: string;
  // See `resolveDagDisplayName` — not trusted from the trigger call's own args.
  dagDisplayName: string | undefined;
}

// Encodes (dagId, runId) as one string; encodeURIComponent escapes ':' so a
// real id can never produce a literal '::' and corrupt the separator.
export function encodePublicDatasetRunKey(dagId: string, runId: string): string {
  return `${encodeURIComponent(dagId)}::${encodeURIComponent(runId)}`;
}
export function decodePublicDatasetRunKey(key: string): { dagId: string; runId: string } {
  const sep = key.indexOf("::");
  return sep === -1
    ? { dagId: decodeURIComponent(key), runId: "" }
    : {
        dagId: decodeURIComponent(key.slice(0, sep)),
        runId: decodeURIComponent(key.slice(sep + 2)),
      };
}

interface SessionPublicDatasetRunsContextValue {
  runs: SessionPublicDatasetRun[];
  setRuns: (runs: SessionPublicDatasetRun[]) => void;
}

// Shared by sibling `Chat` (derives the list) and `SessionContextSidebar`
// (renders it) — lives in their common ancestor. Mirrors `SessionEvalRunsContext`.
const SessionPublicDatasetRunsContext = createContext<SessionPublicDatasetRunsContextValue | null>(
  null
);

export const SessionPublicDatasetRunsProvider = ({ children }: { children: React.ReactNode }) => {
  const [runs, setRuns] = useState<SessionPublicDatasetRun[]>([]);

  return (
    <SessionPublicDatasetRunsContext.Provider value={{ runs, setRuns }}>
      {children}
    </SessionPublicDatasetRunsContext.Provider>
  );
};

// Prefers the result's own dag_id over the call args (ground truth vs.
// fallback) — the result is Airflow's real DAGRun object. A refusal result
// is a plain string, not JSON; return null rather than crash.
function parseTriggerResult(
  raw: string,
  argsDagId: string
): { dagId: string; runId: string } | null {
  try {
    const parsed: unknown = JSON.parse(raw);
    if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
      const rec = parsed as { dag_run_id?: unknown; dag_id?: unknown };
      if (typeof rec.dag_run_id === "string" && rec.dag_run_id.length > 0) {
        const dagId =
          typeof rec.dag_id === "string" && rec.dag_id.length > 0 ? rec.dag_id : argsDagId;
        return { dagId, runId: rec.dag_run_id };
      }
    }
  } catch {
    // Not JSON — a refusal string. Nothing to recover.
  }
  return null;
}

// Pure function of conversation history: every successful (non-refusal)
// trigger call contributes its (dag_id, run_id), de-duped, first-seen order.
export function sessionPublicDatasetRunsFromMessages(
  messages: ReadonlyArray<Message>
): SessionPublicDatasetRun[] {
  const resultByCallId = new Map<string, string>();
  for (const m of messages) {
    const tm = m as { role?: string; toolCallId?: string; content?: string };
    if (tm.role === "tool" && tm.toolCallId && typeof tm.content === "string") {
      resultByCallId.set(tm.toolCallId, tm.content);
    }
  }

  // Same ground-truth resolution as PublicDatasetTriggerCard, but across the
  // whole session, not one turn.
  const listingToolCalls: ToolCallEntry[] = messages.flatMap((m) => {
    const calls = (
      m as { toolCalls?: { id: string; function: { name: string; arguments: string } }[] }
    ).toolCalls;
    return (calls ?? [])
      .filter((tc) => tc.function.name === LIST_PIPELINES_TOOL_NAME && resultByCallId.has(tc.id))
      .map((tc) => ({
        id: tc.id,
        name: tc.function.name,
        args: tc.function.arguments,
        result: resultByCallId.get(tc.id),
      }));
  });

  const seen = new Set<string>();
  const runs: SessionPublicDatasetRun[] = [];
  for (const m of messages) {
    const calls = (
      m as { toolCalls?: { id: string; function: { name: string; arguments: string } }[] }
    ).toolCalls;
    if (!calls) {
      continue;
    }
    for (const tc of calls) {
      if (tc.function.name !== TRIGGER_RUN_TOOL_NAME) {
        continue;
      }
      const args = parseTriggerArgs(tc.function.arguments);
      if (!args) {
        continue;
      }
      const raw = resultByCallId.get(tc.id);
      if (raw === undefined) {
        continue;
      }
      const parsed = parseTriggerResult(raw, args.dagId);
      if (!parsed) {
        continue;
      }
      const key = encodePublicDatasetRunKey(parsed.dagId, parsed.runId);
      if (seen.has(key)) {
        continue;
      }
      seen.add(key);
      runs.push({
        dagId: parsed.dagId,
        runId: parsed.runId,
        dagDisplayName: resolveDagDisplayName(listingToolCalls, parsed.dagId),
      });
    }
  }
  return runs;
}

async function isPublicDatasetRunInFlight(
  dagId: string,
  runId: string,
  signal: AbortSignal
): Promise<boolean> {
  const resp = await apiClient<{ state: string }>(publicDatasetRunStatusPath(dagId, runId), {
    signal,
  });
  return !AIRFLOW_RUN_TERMINAL_STATES.has(resp.state);
}

// Mirrors `useSessionEvalRunsSync`: a live trigger opens+focuses immediately;
// ids present at mount are a reload, focused only if a poll shows in-flight.
//
// No client-side role gate (unlike eval runs) — the backend's can_use +
// conf.triggered_by gates already scope this to the triggering user's session.
export function useSessionPublicDatasetRunsSync(messages: ReadonlyArray<Message>): void {
  const ctx = useContext(SessionPublicDatasetRunsContext);
  const { openPanel } = useSessionContextPanel();
  const seenRef = useRef<Set<string>>(new Set());
  const mountCapturedRef = useRef(false);

  const runs = useMemo(() => sessionPublicDatasetRunsFromMessages(messages), [messages]);

  useEffect(() => {
    ctx?.setRuns(runs);
  }, [ctx, runs]);

  useEffect(() => {
    const keyOf = (r: SessionPublicDatasetRun) => encodePublicDatasetRunKey(r.dagId, r.runId);

    if (!mountCapturedRef.current) {
      mountCapturedRef.current = true;
      runs.forEach((r) => seenRef.current.add(keyOf(r)));
      const last = runs[runs.length - 1];
      if (!last) {
        return;
      }
      const controller = new AbortController();
      isPublicDatasetRunInFlight(last.dagId, last.runId, controller.signal)
        .then((inFlight) => {
          if (!controller.signal.aborted && inFlight) {
            openPanel(SessionContextPanelType.PUBLIC_DATASET_RUN, keyOf(last));
          }
        })
        .catch(() => {
          // Status unknown (transient failure / run gone) → don't steal
          // focus; the row still shows in the TOC and its own poll catches up.
        });
      return () => controller.abort();
    }

    const fresh = runs.filter((r) => !seenRef.current.has(keyOf(r)));
    if (fresh.length === 0) {
      return;
    }
    fresh.forEach((r) => seenRef.current.add(keyOf(r)));
    openPanel(SessionContextPanelType.PUBLIC_DATASET_RUN, keyOf(fresh[fresh.length - 1]));
  }, [runs, openPanel]);
}

// Read-only projection of the run list; what the sidebar's list/switcher render from.
export function useSessionPublicDatasetRuns(): SessionPublicDatasetRun[] {
  const ctx = useContext(SessionPublicDatasetRunsContext);
  if (!ctx) {
    throw new Error(
      "useSessionPublicDatasetRuns must be used within a SessionPublicDatasetRunsProvider"
    );
  }
  return ctx.runs;
}
